import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  epicsApi,
  type EpicFilters,
  type CreateEpicInput,
  type UpdateEpicInput,
} from '@/lib/api/epics';
import { dependencyDocumentKeys } from './use-dependency-documents';

export const epicKeys = {
  all: ['epics'] as const,
  lists: () => [...epicKeys.all, 'list'] as const,
  list: (filters: EpicFilters) => [...epicKeys.lists(), filters] as const,
  details: () => [...epicKeys.all, 'detail'] as const,
  detail: (id: string) => [...epicKeys.details(), id] as const,
};

export function useEpics(filters: EpicFilters = {}) {
  return useInfiniteQuery({
    queryKey: epicKeys.list(filters),
    queryFn: ({ pageParam }) =>
      epicsApi.list({ ...filters, cursor: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.meta?.hasMore && lastPage.meta.cursor ? lastPage.meta.cursor : undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Hook for page-by-page epic listing (not infinite scroll).
 * Returns a single page of results for the given cursor.
 */
export function useEpicsPaged(filters: EpicFilters = {}) {
  return useQuery({
    queryKey: [...epicKeys.list(filters), 'paged'] as const,
    queryFn: () => epicsApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useEpic(id: string) {
  return useQuery({
    queryKey: epicKeys.detail(id),
    queryFn: () => epicsApi.get(id),
    enabled: !!id,
    select: (response) => response.data,
  });
}

export function useCreateEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEpicInput) => epicsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      // Also invalidate epic requests since the linked request becomes converted
      queryClient.invalidateQueries({ queryKey: ['epicRequests'] });
      // Conversion copies the request's precursor documents onto the new epic,
      // so both parents' document lists are now stale.
      void queryClient.invalidateQueries({ queryKey: dependencyDocumentKeys.lists() });
    },
  });
}

export function useUpdateEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateEpicInput) => epicsApi.update(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.setQueryData(epicKeys.detail(response.data.id), response);
    },
  });
}

export function useDeleteEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
    },
  });
}

export function useArchiveEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.archive(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.setQueryData(epicKeys.detail(response.data.id), response);
    },
  });
}

export function useUnarchiveEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.unarchive(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.setQueryData(epicKeys.detail(response.data.id), response);
    },
  });
}

export function useMarkEpicImplemented() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.markImplemented(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epicKeys.detail(response.data.id) });
    },
  });
}

/**
 * Hook for transferring an epic between personal and team scope
 */
export function useTransferEpicScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { direction: 'personal-to-team' | 'team-to-personal'; teamId?: string } }) =>
      epicsApi.transfer(id, input),
    onSuccess: (_data, variables) => {
      // Invalidate all epic lists (scope changed)
      void queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      // Invalidate the specific detail
      void queryClient.invalidateQueries({ queryKey: epicKeys.detail(variables.id) });
    },
  });
}
